import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  alpha,
  Box,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Typography
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import { useLenis } from 'lenis/react'
import { CalendarDaysIcon, CalendarIcon, EuroIcon, UserIcon, WrenchIcon } from 'lucide-react'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import FormDialog from '@/components/layout/advertisements/form-dialog'
import { SearchPagination } from '@/components/layout/advertisements/search'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { useTranslation } from '@/hooks/use-translation'
import { useLocaleStore } from '@/store/locale'
import { isPostAvailable } from '@/utils/date'
import { formatPrice, truncateText } from '@/utils/strings'

import 'dayjs/locale/it'

import { useCities } from '@/api/cities'
import { PostSearchParams, usePostsSearch } from '@/api/posts'
import { Gender, Post, PostType } from '@/api/posts/types'
import { City, TranslatedField } from '@/api/types'
import { WPPageData } from '@/types'

const POSTS_PER_PAGE = 12
// ponytail: server has no "still available" filter, so we fetch the API max per_page in one
// shot and paginate client-side after filtering by availability. Ceiling: 100 matching posts
// per filter combination (API's per_page max); above that, add server-side support instead.
const SEARCH_FETCH_PER_PAGE = 100

/**
 * Helper: Parse URL query parameter to number
 */
const parseIntParam = (param: string | string[] | undefined): number | undefined => {
  if (!param) return undefined
  const value = Array.isArray(param) ? param[0] : param
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? undefined : parsed
}

/**
 * Types
 */
export type StudentPostsProps = {
  postType: PostType
  pageProps?: WPPageData
}

type PostFilters = {
  available_from: string | undefined
  available_to: string | undefined
  gender: '' | '0' | '1'
  min_price: number | undefined
  max_price: number | undefined
  city_id: number | undefined
  page: number
}

/**
 * Hook: usePostFilters
 */
const usePostFilters = (postType: PostType) => {
  const router = useRouter()

  const getFiltersFromUrl = useCallback((): PostFilters => {
    const q = router.query
    return {
      available_from: (q.available_from as string) || undefined,
      available_to: (q.available_to as string) || undefined,
      gender: (q.gender as PostFilters['gender']) || '',
      min_price: parseIntParam(q.min_price),
      max_price: parseIntParam(q.max_price),
      city_id: parseIntParam(q.city_id),
      page: parseIntParam(q.page) || 1
    }
  }, [router.query])

  const [filters, setFilters] = useState<PostFilters>(getFiltersFromUrl)

  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  useEffect(() => {
    setFilters(getFiltersFromUrl())
  }, [getFiltersFromUrl])

  const searchParams = useMemo<PostSearchParams>(
    () => ({
      post_type: postType,
      available_from: filters.available_from,
      available_to: filters.available_to,
      gender: filters.gender ? (Number(filters.gender) as Gender) : undefined,
      min_price: filters.min_price,
      max_price: filters.max_price,
      city_id: filters.city_id,
      per_page: SEARCH_FETCH_PER_PAGE,
      order_by: '-date'
    }),
    [filters, postType]
  )

  const updateUrl = useCallback(
    (newFilters: Partial<PostFilters>) => {
      const updated = { ...filtersRef.current, ...newFilters }
      const query: Record<string, string> = {}

      if (updated.available_from) query.available_from = updated.available_from
      if (updated.available_to) query.available_to = updated.available_to
      if (updated.gender) query.gender = updated.gender
      if (updated.min_price !== undefined) query.min_price = String(updated.min_price)
      if (updated.max_price !== undefined) query.max_price = String(updated.max_price)
      if (updated.city_id !== undefined) query.city_id = String(updated.city_id)
      if (updated.page > 1) query.page = String(updated.page)

      const pathname = router.asPath.split('?')[0]
      router.push({ pathname, query }, undefined, { shallow: true })
    },
    [router]
  )

  const updateFilter = useCallback(
    <K extends keyof PostFilters>(key: K, value: PostFilters[K], resetPage = true) => {
      const updates: Partial<PostFilters> = { [key]: value }
      if (resetPage && key !== 'page') updates.page = 1
      setFilters((prev) => ({ ...prev, ...updates }))
      updateUrl(updates)
    },
    [updateUrl]
  )

  const setAvailableFrom = useCallback(
    (value: string | undefined) => updateFilter('available_from', value),
    [updateFilter]
  )
  const setAvailableTo = useCallback(
    (value: string | undefined) => updateFilter('available_to', value),
    [updateFilter]
  )
  const setGender = useCallback(
    (value: PostFilters['gender']) => updateFilter('gender', value),
    [updateFilter]
  )
  const setMinPrice = useCallback(
    (value: number | undefined) => updateFilter('min_price', value),
    [updateFilter]
  )
  const setMaxPrice = useCallback(
    (value: number | undefined) => updateFilter('max_price', value),
    [updateFilter]
  )
  const setCityId = useCallback(
    (value: number | undefined) => updateFilter('city_id', value),
    [updateFilter]
  )
  const setPage = useCallback((value: number) => updateFilter('page', value, false), [updateFilter])

  return {
    filters,
    searchParams,
    setAvailableFrom,
    setAvailableTo,
    setGender,
    setMinPrice,
    setMaxPrice,
    setCityId,
    setPage
  }
}

/**
 * PRICE_OPTIONS
 */
const PRICE_OPTIONS = [
  { value: '', label: '' },
  { value: '100', label: '100,00 €' },
  { value: '200', label: '200,00 €' },
  { value: '300', label: '300,00 €' },
  { value: '400', label: '400,00 €' },
  { value: '500', label: '500,00 €' },
  { value: '600', label: '600,00 €' },
  { value: '700', label: '700,00 €' },
  { value: '800', label: '800,00 €' },
  { value: '1000', label: '1000,00 €+' }
]

/**
 * Component: PostFiltersBar
 */
type PostFiltersBarProps = {
  availableFrom: string | undefined
  availableTo: string | undefined
  gender: '' | '0' | '1'
  minPrice: number | undefined
  maxPrice: number | undefined
  cityId: number | undefined
  onAvailableFromChange: (_value: string | undefined) => void
  onAvailableToChange: (_value: string | undefined) => void
  onGenderChange: (_value: '' | '0' | '1') => void
  onMinPriceChange: (_value: number | undefined) => void
  onMaxPriceChange: (_value: number | undefined) => void
  onCityIdChange: (_value: number | undefined) => void
}

const PostFiltersBar = ({
  availableFrom,
  availableTo,
  gender,
  minPrice,
  maxPrice,
  cityId,
  onAvailableFromChange,
  onAvailableToChange,
  onGenderChange,
  onMinPriceChange,
  onMaxPriceChange,
  onCityIdChange
}: PostFiltersBarProps) => {
  const [isMounted, setIsMounted] = useState(false)
  const { t } = useTranslation('common')
  const { strings } = useAppStore()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { collection: cities } = useCities({ page: 0, per_page: 100, onlyWithAdvertisements: true })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={3}
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      flexWrap="wrap"
      useFlexGap
    >
      {/* Città (solo isHappa) */}
      {isHappa && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography variant="caption" fontWeight="500" color="text.secondary">
            {t('city.label')}
          </Typography>
          <FormControl size="small">
            <Select
              value={cityId?.toString() ?? ''}
              onChange={(e: SelectChangeEvent<string>) =>
                onCityIdChange(e.target.value ? Number(e.target.value) : undefined)
              }
              variant="standard"
              displayEmpty
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': { py: 1 }
              }}
            >
              <MenuItem value="">{t('city.allCities')}</MenuItem>
              {(cities || []).map((city: City) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name?.[currentLocale || 'it'] || city.name?.it || city.name?.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Disponibile dal */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="caption" fontWeight="500" color="text.secondary">
          {strings?.labels?.available_from}
        </Typography>
        {isMounted ? (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale || 'it'}>
            <DatePicker
              value={availableFrom ? dayjs(availableFrom) : null}
              onChange={(newValue) => {
                onAvailableFromChange(
                  newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : undefined
                )
              }}
              slots={{
                openPickerIcon: () => <CalendarDaysIcon size={16} />
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: {
                    borderRadius: (theme) => theme.shape.borderRadius2,
                    '& .MuiInputBase-root': {
                      py: 1
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        ) : (
          <TextField size="small" variant="standard" disabled placeholder={t('post.selectDate')} />
        )}
      </Box>

      {/* Disponibile fino al */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="caption" fontWeight="500" color="text.secondary">
          {strings?.labels?.available_until}
        </Typography>
        {isMounted ? (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={currentLocale || 'it'}>
            <DatePicker
              value={availableTo ? dayjs(availableTo) : null}
              onChange={(newValue) => {
                onAvailableToChange(
                  newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : undefined
                )
              }}
              slots={{
                openPickerIcon: () => <CalendarDaysIcon size={16} />
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: {
                    borderRadius: (theme) => theme.shape.borderRadius2,
                    '& .MuiInputBase-root': {
                      py: 1
                    }
                  }
                }
              }}
            />
          </LocalizationProvider>
        ) : (
          <TextField size="small" variant="standard" disabled placeholder={t('post.selectDate')} />
        )}
      </Box>

      {/* Genere */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="caption" fontWeight="500" color="text.secondary">
          {strings?.labels?.gender}
        </Typography>
        <FormControl size="small">
          <Select
            value={gender}
            onChange={(e: SelectChangeEvent<string>) =>
              onGenderChange(e.target.value as PostFiltersBarProps['gender'])
            }
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            <MenuItem value="">{strings?.labels?.any}</MenuItem>
            <MenuItem value="0">{strings?.labels?.female}</MenuItem>
            <MenuItem value="1">{strings?.labels?.male}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Prezzo mensile */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="caption" fontWeight="500" color="text.secondary">
          {strings?.labels?.monthly_price}
        </Typography>
        <Stack direction="row" spacing={4} alignItems="center">
          <FormControl size="small" sx={{ width: 120 }}>
            <Select
              value={minPrice?.toString() ?? ''}
              onChange={(e: SelectChangeEvent<string>) =>
                onMinPriceChange(e.target.value ? Number(e.target.value) : undefined)
              }
              variant="standard"
              displayEmpty
              renderValue={(selected) =>
                selected ? PRICE_OPTIONS.find((o) => o.value === selected)?.label : t('post.min')
              }
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': {
                  py: 1
                }
              }}
            >
              {PRICE_OPTIONS.map((option) => (
                <MenuItem key={`min-${option.value}`} value={option.value}>
                  {option.value ? option.label : strings?.labels?.any}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: 120 }}>
            <Select
              value={maxPrice?.toString() ?? ''}
              onChange={(e: SelectChangeEvent<string>) =>
                onMaxPriceChange(e.target.value ? Number(e.target.value) : undefined)
              }
              variant="standard"
              displayEmpty
              renderValue={(selected) =>
                selected ? PRICE_OPTIONS.find((o) => o.value === selected)?.label : t('post.max')
              }
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': {
                  py: 1
                }
              }}
            >
              {PRICE_OPTIONS.map((option) => (
                <MenuItem key={`max-${option.value}`} value={option.value}>
                  {option.value ? option.label : strings?.labels?.any}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
    </Stack>
  )
}

/**
 * Component: PostCard
 */
type PostCardProps = {
  post: Post
  currentLocale: string
  monthLabel: string | undefined
}

const PostCard = ({ post, currentLocale, monthLabel }: PostCardProps) => {
  const locale = currentLocale as keyof TranslatedField
  const [contactOpen, setContactOpen] = useState(false)
  const { strings } = useAppStore()
  const { t } = useTranslation('auth')
  const { t: tc } = useTranslation('common')

  const genderLabels = useMemo(
    () => ({
      0: t('forms.female') || 'Femmina',
      1: t('forms.male') || 'Maschio',
      2: t('forms.other') || 'Altro'
    }),
    [t]
  )
  const profileTypeLabels = useMemo(
    () => ({
      0: t('forms.student') || 'Studente',
      1: t('forms.professor') || 'Professore',
      2: t('forms.phdStudent') || 'Dottorando',
      3: t('forms.researcher') || 'Ricercatore'
    }),
    [t]
  )
  const accommodationTypeLabels = useMemo(
    () => ({
      0: t('forms.apartment') || 'Appartamento',
      1: t('forms.room') || 'Camera',
      2: t('forms.bed') || 'Posto letto'
    }),
    [t]
  )
  const roomTypeLabels = useMemo(
    () => ({
      0: t('forms.single') || 'Singola',
      1: t('forms.double') || 'Doppia',
      2: t('forms.triple') || 'Tripla'
    }),
    [t]
  )
  const contractTypeLabels = useMemo(
    () => ({
      0: t('forms.subentry') || 'Subentro',
      1: t('forms.sublease') || 'Subaffitto'
    }),
    [t]
  )
  const postTypeTagLabels = useMemo(
    () => ({
      0: t('forms.offer') || 'Offerta',
      1: t('forms.request') || 'Richiesta'
    }),
    [t]
  )

  const postSummary = useMemo(() => {
    const lines: string[] = []
    const userName = [post.user?.first_name, post.user?.last_name].filter(Boolean).join(' ')
    if (userName) lines.push(`${tc('post.summary.user')}: ${userName}`)
    if (post.city?.name?.[locale])
      lines.push(`${tc('post.summary.city')}: ${post.city.name[locale]}`)
    if (post.gender != null && genderLabels[post.gender])
      lines.push(`${tc('post.summary.gender')}: ${genderLabels[post.gender]}`)
    if (post.profile_type != null && profileTypeLabels[post.profile_type])
      lines.push(`${tc('post.summary.profile')}: ${profileTypeLabels[post.profile_type]}`)
    if (post.accommodation_type != null && accommodationTypeLabels[post.accommodation_type])
      lines.push(
        `${tc('post.summary.accommodation')}: ${accommodationTypeLabels[post.accommodation_type]}`
      )
    if (post.contract_type != null && contractTypeLabels[post.contract_type])
      lines.push(`${tc('post.summary.contract')}: ${contractTypeLabels[post.contract_type]}`)
    if (post.room_type != null && roomTypeLabels[post.room_type])
      lines.push(`${tc('post.summary.room')}: ${roomTypeLabels[post.room_type]}`)
    if (post.price)
      lines.push(`${tc('post.summary.price')}: ${formatPrice(post.price)}/${monthLabel}`)
    if (post.available_from)
      lines.push(
        `${tc('post.summary.availableFrom')}: ${new Date(post.available_from).toLocaleDateString(currentLocale === 'it' ? 'it-IT' : 'en-GB')}`
      )
    if (post.available_to)
      lines.push(
        `${tc('post.summary.availableTo')}: ${new Date(post.available_to).toLocaleDateString(currentLocale === 'it' ? 'it-IT' : 'en-GB')}`
      )
    if (post.included_services?.length) {
      const services = post.included_services
        .map((s) => s.name?.[locale] || s.name?.it || s.name?.en)
        .filter(Boolean)
        .join(', ')
      if (services) lines.push(`${tc('post.summary.includedServices')}: ${services}`)
    }
    if (post.description?.[locale])
      lines.push(`\n${tc('post.summary.description')}:\n${post.description[locale]}`)
    return lines.join('\n')
  }, [
    post,
    locale,
    currentLocale,
    monthLabel,
    genderLabels,
    profileTypeLabels,
    accommodationTypeLabels,
    contractTypeLabels,
    roomTypeLabels,
    tc
  ])

  const includedServicesLabel = useMemo(() => {
    if (!post.included_services?.length) return null
    const services = post.included_services
      .map((service) => service.name?.[locale] || service.name?.it || service.name?.en)
      .filter(Boolean)
      .join(', ')
    return services || null
  }, [post.included_services, locale])

  return (
    <Grid size={{ xs: 4, sm: 4, md: 4 }}>
      <Box
        sx={(theme) => ({
          p: 2,
          pb: 4,
          borderRadius: isHappa ? theme.shape.borderRadius2 : theme.shape.borderRadius8,
          background: isHappa ? '#ffffff' : theme.palette.grey[100],
          boxShadow: isHappa ? theme.shadows[1] : 'none',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={(theme) => ({
              background: theme.palette.primary.main,
              color: theme.palette.surface.background,
              width: '4rem',
              height: '4rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '100px'
            })}
          >
            <SvgIcon>
              <use href="#ico-student" />
            </SvgIcon>
          </Box>
          <Chip
            size="small"
            label={postTypeTagLabels[post.post_type] || 'Offerta'}
            color="primary"
            variant="outlined"
          />
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            flex: 1
          }}
        >
          {post.user?.first_name || post.user?.last_name ? (
            <Typography variant="textLarge" fontWeight={500} component="span">
              {[post.user?.first_name, post.user?.last_name].filter(Boolean).join(' ')}
            </Typography>
          ) : null}
          {post.price ? (
            <Typography variant="textLarge" color="primary" fontWeight={500} component="span">
              {`${formatPrice(post.price)}/${monthLabel}`}
            </Typography>
          ) : null}

          {post.available_from ||
          post.available_to ||
          post.city?.name?.[locale] ||
          (post.accommodation_type != null && accommodationTypeLabels[post.accommodation_type]) ||
          (post.contract_type != null && contractTypeLabels[post.contract_type]) ||
          (post.gender != null && genderLabels[post.gender]) ||
          (post.profile_type != null && profileTypeLabels[post.profile_type]) ||
          (post.room_type != null && roomTypeLabels[post.room_type]) ||
          includedServicesLabel ? (
            <Stack spacing={0.5}>
              {post.available_from || post.available_to ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.from}:{' '}
                  <strong>
                    {post.available_from
                      ? new Date(post.available_from).toLocaleDateString(
                          currentLocale === 'it' ? 'it-IT' : 'en-GB'
                        )
                      : '—'}{' '}
                  </strong>
                  {strings?.labels?.to}:{' '}
                  <strong>
                    {post.available_to
                      ? new Date(post.available_to).toLocaleDateString(
                          currentLocale === 'it' ? 'it-IT' : 'en-GB'
                        )
                      : '—'}
                  </strong>
                </Typography>
              ) : null}
              {post.city?.name?.[locale] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {tc('city.label')}: <strong>{post.city.name[locale]}</strong>
                </Typography>
              ) : null}
              {post.accommodation_type != null &&
              accommodationTypeLabels[post.accommodation_type] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.accomodation}:{' '}
                  <strong>{accommodationTypeLabels[post.accommodation_type]}</strong>
                </Typography>
              ) : null}
              {post.contract_type != null && contractTypeLabels[post.contract_type] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.contract_type}:{' '}
                  <strong>{contractTypeLabels[post.contract_type]}</strong>
                </Typography>
              ) : null}
              {post.gender != null && genderLabels[post.gender] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.gender}: <strong>{genderLabels[post.gender]}</strong>
                </Typography>
              ) : null}
              {post.profile_type != null && profileTypeLabels[post.profile_type] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.profile}:{' '}
                  <strong>{profileTypeLabels[post.profile_type]}</strong>
                </Typography>
              ) : null}
              {post.room_type != null && roomTypeLabels[post.room_type] ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.room}: <strong>{roomTypeLabels[post.room_type]}</strong>
                </Typography>
              ) : null}
              {includedServicesLabel ? (
                <Typography variant="textSmall" color="text.secondary" component="span">
                  {strings?.labels?.included_services}: <strong>{includedServicesLabel}</strong>
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {post.description?.[locale] ? (
            <Typography variant="textRegular" color="text.secondary" component="p">
              {truncateText(post.description[locale], 200)}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          color="primary"
          icon="send-message"
          onClick={() => setContactOpen(true)}
        >
          {strings?.labels?.contacts}
        </Button>
      </Box>

      <FormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        subjectType="post"
        subjectId={post.id}
        modalId="contact"
        isStudentRequest
        subjectSummary={postSummary}
        summaryContent={
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 2
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06)
              }}
            >
              {post.user?.first_name || post.user?.last_name ? (
                <Typography variant="textLarge" fontWeight={600} color="text.primary">
                  {[post.user?.first_name, post.user?.last_name].filter(Boolean).join(' ')}
                </Typography>
              ) : null}
            </Box>

            <Divider />

            {/* Details */}
            <Stack spacing={1.5} sx={{ px: 2, py: 1.5 }}>
              {/* Price */}
              {post.price ? (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <EuroIcon size={15} />
                  <Typography variant="textSmall">
                    <strong>{formatPrice(post.price)}</strong> / {monthLabel}
                  </Typography>
                </Stack>
              ) : null}

              {/* Availability range */}
              {post.available_from || post.available_to ? (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <CalendarIcon size={15} />
                  <Typography variant="textSmall">
                    {strings?.labels?.availability}:{' '}
                    {post.available_from
                      ? new Date(post.available_from).toLocaleDateString(
                          currentLocale === 'it' ? 'it-IT' : 'en-GB'
                        )
                      : '—'}{' '}
                    →{' '}
                    {post.available_to
                      ? new Date(post.available_to).toLocaleDateString(
                          currentLocale === 'it' ? 'it-IT' : 'en-GB'
                        )
                      : '—'}
                  </Typography>
                </Stack>
              ) : null}

              {/* Profile info chips */}
              {post.city?.name?.[locale] ||
              post.gender != null ||
              post.profile_type != null ||
              post.accommodation_type != null ||
              post.contract_type != null ||
              post.room_type != null ? (
                <>
                  <Divider />
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                      <UserIcon size={15} />
                      <Typography variant="textSmall" fontWeight={600}>
                        {strings?.labels?.details}
                      </Typography>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {post.city?.name?.[locale] ? (
                        <Chip label={post.city.name[locale]} size="small" variant="outlined" />
                      ) : null}
                      {post.gender != null && genderLabels[post.gender] ? (
                        <Chip label={genderLabels[post.gender]} size="small" variant="outlined" />
                      ) : null}
                      {post.profile_type != null && profileTypeLabels[post.profile_type] ? (
                        <Chip
                          label={profileTypeLabels[post.profile_type]}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : null}
                      {post.accommodation_type != null &&
                      accommodationTypeLabels[post.accommodation_type] ? (
                        <Chip
                          label={accommodationTypeLabels[post.accommodation_type]}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                      {post.contract_type != null && contractTypeLabels[post.contract_type] ? (
                        <Chip
                          label={contractTypeLabels[post.contract_type]}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                      {post.room_type != null && roomTypeLabels[post.room_type] ? (
                        <Chip
                          label={roomTypeLabels[post.room_type]}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                  </Box>
                </>
              ) : null}

              {/* Included services */}
              {post.included_services?.length ? (
                <>
                  <Divider />
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                      <WrenchIcon size={15} />
                      <Typography variant="textSmall" fontWeight={600}>
                        {strings?.labels?.included_services}
                      </Typography>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {post.included_services.map((service) => (
                        <Chip
                          key={service.id}
                          label={service.name?.[locale] || service.name?.it || service.name?.en}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>
                </>
              ) : null}

              {/* Description */}
              {post.description?.[locale] ? (
                <>
                  <Divider />
                  <Typography
                    variant="textSmall"
                    color="text.secondary"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {post.description[locale]}
                  </Typography>
                </>
              ) : null}
            </Stack>
          </Box>
        }
      />
    </Grid>
  )
}

const PostCardSkeleton = ({ index }: { index: number }) => (
  <Grid size={{ xs: 4, sm: 4, md: 4 }} key={`skeleton-${index}`}>
    <Box
      sx={(theme) => ({
        p: 2,
        pb: 4,
        borderRadius: theme.shape.borderRadius8,
        background: theme.palette.grey[100],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2
      })}
    >
      <Skeleton variant="circular" width={40} height={40} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          width: '100%'
        }}
      >
        <Skeleton variant="text" width="80%" height={24} />
        <Skeleton variant="text" width="100%" height={42} />
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="100%" height={42} />
      </Box>
    </Box>
  </Grid>
)

/**
 * Component: PostResults
 */
type PostResultsProps = {
  isLoading: boolean
  collection: Post[] | undefined
  currentLocale: string
  monthLabel: string | undefined
}

const PostResults = ({ isLoading, collection, currentLocale, monthLabel }: PostResultsProps) => {
  const { t } = useTranslation('common')
  if (isLoading) {
    return (
      <>
        {Array.from({ length: POSTS_PER_PAGE }).map((_, i) => (
          <PostCardSkeleton key={i} index={i} />
        ))}
      </>
    )
  }

  if (!collection?.length) {
    return (
      <Grid size={{ xs: 4, sm: 8, md: 12 }}>
        <Typography variant="textLarge" color="text.secondary" sx={{ py: 4 }}>
          {t('post.noResults')}
        </Typography>
      </Grid>
    )
  }

  return (
    <>
      {collection.map((post) => (
        <PostCard key={post.id} post={post} currentLocale={currentLocale} monthLabel={monthLabel} />
      ))}
    </>
  )
}

/**
 * Main Component: StudentPosts
 */
const StudentPosts = ({ postType, pageProps }: StudentPostsProps) => {
  const lenis = useLenis()
  const { t } = useTranslation('common')
  const currentLocale = useLocaleStore((state) => state.currentLocale) || 'it'
  const { strings } = useAppStore()
  const titleRendered = useMemo(() => pageProps?.title, [pageProps])
  const contentRendered = useMemo(() => pageProps?.content?.rendered, [pageProps])

  const {
    filters,
    searchParams,
    setAvailableFrom,
    setAvailableTo,
    setGender,
    setMinPrice,
    setMaxPrice,
    setCityId,
    setPage
  } = usePostFilters(postType)

  const { collection: rawCollection, isLoading } = usePostsSearch(searchParams)
  const availablePosts = useMemo(
    () => rawCollection?.filter((post) => isPostAvailable(post.available_to)),
    [rawCollection]
  )

  const pagination = useMemo(
    () =>
      availablePosts
        ? {
            total: availablePosts.length,
            last_page: Math.max(1, Math.ceil(availablePosts.length / POSTS_PER_PAGE))
          }
        : undefined,
    [availablePosts]
  )
  const collection = useMemo(
    () =>
      availablePosts?.slice((filters.page - 1) * POSTS_PER_PAGE, filters.page * POSTS_PER_PAGE),
    [availablePosts, filters.page]
  )

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      setPage(page)
      lenis?.scrollTo(0, { immediate: true })
    },
    [setPage, lenis]
  )

  return (
    <>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 8, md: 12 }}>
            <Typography
              variant="h2"
              component="h1"
              color={isHappa ? 'primary.main' : 'text.secondaryContrastText'}
              fontWeight={isHappa ? 700 : 500}
            >
              {titleRendered?.rendered ||
                (postType === 1
                  ? t('post.lookingForAccommodation')
                  : t('post.offeringAccommodation'))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4, sm: 8, md: 6 }}>
            {contentRendered ? (
              <Typography
                variant="textRegular"
                color="text.secondary"
                component="div"
                sx={{ mt: 2 }}
                dangerouslySetInnerHTML={{ __html: contentRendered }}
              />
            ) : null}
          </Grid>
        </Grid>
      </Container>

      <Spacer size="sm" />

      <Container>
        <PostFiltersBar
          availableFrom={filters.available_from}
          availableTo={filters.available_to}
          gender={filters.gender}
          minPrice={filters.min_price}
          maxPrice={filters.max_price}
          cityId={filters.city_id}
          onAvailableFromChange={setAvailableFrom}
          onAvailableToChange={setAvailableTo}
          onGenderChange={setGender}
          onMinPriceChange={setMinPrice}
          onMaxPriceChange={setMaxPrice}
          onCityIdChange={setCityId}
        />
      </Container>

      <Spacer size="md" />

      <Container>
        <Grid container>
          <PostResults
            isLoading={isLoading}
            collection={collection}
            currentLocale={currentLocale}
            monthLabel={strings?.labels?.month}
          />
        </Grid>
      </Container>

      <Spacer size="2xl" />

      {pagination && pagination.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SearchPagination
            count={pagination.last_page}
            page={filters.page}
            onChange={handlePageChange}
          />
        </Box>
      )}

      <Spacer size="2xl" />
    </>
  )
}

export default StudentPosts
